import SessionFeedbackPopover from "./SessionFeedbackPopover";
import Thumb from "./Thumb";
import BadRequestErrorResponse from "@app/config/api/responses/BadRequestErrorResponse";
import posthogEvents from "@app/config/posthog/posthog-events";
import { useSubmitSessionFeedback } from "@app/queries/agent";
import type { AgentSessionFeedbackRating, AgentSessionId } from "@globals";
import { notifications } from "@lib/notification/notification";
import { Group, Text, Transition } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import * as Sentry from "@sentry/react";
import { usePostHog } from "posthog-js/react";
import { useState } from "react";

// `delta_blocked` covers server-rejection feedback; the local count check at `isDeltaBlocked`
// covers the post-success wait. Both fan into `isDeltaBlocked` — update them together.
type State = { kind: "idle" } | { kind: "expanded"; rating: AgentSessionFeedbackRating } | { kind: "delta_blocked" };

type Props = {
  agentSessionId: AgentSessionId;
  disabled?: boolean;
  messageCount: number;
};

export default function ChatSessionUserFeedback({ agentSessionId, disabled, messageCount }: Props) {
  const posthog = usePostHog();
  const [state, setState] = useState<State>({ kind: "idle" });
  // Track how many messages existed when feedback was last submitted so the
  // delta-rule block auto-clears once the user sends a new message.
  const [submittedAtCount, setSubmittedAtCount] = useState<number | null>(null);
  const [comment, setComment] = useState<string>("");
  const [feedbackPopoverOpened, feedbackPopoverControl] = useDisclosure(false);
  const submitMutation = useSubmitSessionFeedback();

  // Auto-clear delta_blocked once a new message arrives after submission.
  const isDeltaBlocked =
    state.kind === "delta_blocked" || (submittedAtCount !== null && messageCount <= submittedAtCount);

  const thumbsDisabled = disabled || isDeltaBlocked;

  function handleThumbClick(rating: AgentSessionFeedbackRating) {
    if (thumbsDisabled) {
      return;
    }
    posthog.capture(posthogEvents.experiment_agent.session_rating_click, { rating });
    if (state.kind === "expanded" && state.rating === rating) {
      setState({ kind: "idle" });
    } else {
      feedbackPopoverControl.open();
      setState({ kind: "expanded", rating });
    }
  }

  async function handleSubmit() {
    if (state.kind !== "expanded") {
      return;
    }
    const trimmedComment = comment.trim();
    try {
      await submitMutation.mutateAsync({
        body: {
          agent_session_id: agentSessionId,
          rating: state.rating,
          comment: trimmedComment
        }
      });
      setSubmittedAtCount(messageCount);
      setState({ kind: "idle" });
      setComment("");
      feedbackPopoverControl.close();
      notifications.success("Feedback sent", "Thank you for your feedback.");
    } catch (err) {
      if (err instanceof BadRequestErrorResponse && err.extra?.code === "delta_rule_violated") {
        setState({ kind: "delta_blocked" });
        feedbackPopoverControl.close();
      } else {
        Sentry.captureException(err);
        notifications.error("Failed to submit your feedback", "Please try again.");
      }
    }
  }

  function handleCancel() {
    setState({ kind: "idle" });
    setComment("");
    feedbackPopoverControl.close();
  }

  return (
    <Transition mounted={!thumbsDisabled && !isDeltaBlocked} transition="fade">
      {transitionStyle => (
        <Group gap="xs" style={transitionStyle}>
          <Text c="dimmed" size="xs">
            Was this session helpful?
          </Text>
          <SessionFeedbackPopover
            opened={feedbackPopoverOpened}
            mode={state.kind === "expanded" && state.rating === "+1" ? "positive" : "negative"}
            comment={comment}
            onCommentChange={setComment}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            onDismiss={handleCancel}
            loading={submitMutation.isPending}
          >
            <Group gap={0}>
              <Thumb
                type="thumbsUp"
                disabled={thumbsDisabled}
                onClick={() => handleThumbClick("+1")}
                selected={state.kind === "expanded" && state.rating === "+1"}
              />
              <Thumb
                type="thumbsDown"
                disabled={thumbsDisabled}
                onClick={() => handleThumbClick("-1")}
                selected={state.kind === "expanded" && state.rating === "-1"}
              />
            </Group>
          </SessionFeedbackPopover>
        </Group>
      )}
    </Transition>
  );
}
